/**
 * car-catalog/picker.js — <x-car-picker>: make → model → year (→ generation) → engine,
 * one shared catalogue for every car tool (docs/why/car-catalog.md, «Выбор машины v2»).
 *
 * v3 (Farid 29.10: «отдельное поле с указанием марки, модели… по алфавиту»):
 *   · TWO FIELDS, «Марка» and «Модель», one list below them; the model field is
 *     locked until a make is chosen. Makes: recent → «Популярные» (the market's
 *     list) → all A–Z; models A–Z; typing filters (catalog.js keeps every order
 *     rule). A model named outright in the make field («гелик», «kia rio») sets
 *     both. Backspace in the empty model field goes back to the make field.
 * v2 (28.10): the year, generation, engine and fuel as facts (below).
 *   · then the YEAR, limited to the model's production years when known; the
 *     year picks the generation, which is asked only when the year belongs to
 *     several of them;
 *   · ENGINE and FUEL as facts when the data knows one (./car-facts.js: fact /
 *     chips / ask); the engine is never guessed among several;
 *   · «По VIN» fills what a VIN can say (the make, a reliable year);
 *   · the value carries `bodies` and `fuels` the data knows, for the forms
 *     that ask the body (the garage, /car-logbook).
 *
 * The page gets a picker by rendering the Blade component; this module mounts
 * every [data-car-picker] on the page and exposes `el.carPicker`:
 *
 *   el.carPicker.value()            → { makeId, modelId, generationId, engineId, year,
 *                                       community, labels:{make, model, generation, engine},
 *                                       label, text }
 *   el.carPicker.setCoverage(fn)    — fn(value) → false when THIS tool has no data for the
 *                                     car: the picker says so and offers «I need it» (a
 *                                     count, filed as car-coverage)
 *   el.carPicker.clear()
 *   el.carPicker.select({modelId, generationId?, engineId?, year?}) — put a car in from outside
 *                                     (a shared link, a «models we cover» chip); the same path
 *                                     as a click on an option
 *   el.carPicker.selectMake(makeId) / setText(text) — a make alone, or words as typed
 *   'car-picker:change' on el       — detail = value()   ('car-picker:ready' once mounted)
 *
 * The last chosen car is kept under one key for ALL car tools
 * (__toolSettings 'car-catalog' / 'last'): a car chosen in /obd2-codes is
 * already there in the next car tool.
 *
 * The GARAGE (./garage.js, from /api/session-bootstrap) fills a page that has
 * not chosen a car itself with its MAIN car: the browser's last car wins only
 * when it is newer than the main car's last save (choosePrefill). The garage is
 * also the browser logbook's cars (/car-logbook, IndexedDB — ./car-context.js
 * logbookPickerCars, loaded only when its localStorage note says there are cars):
 * a guest's logbook car fills the page and switches like an account car; an
 * account car the logbook keeps too is listed once. A garage car
 * shows «Из вашего гаража: Haval Jolion ▾» — a switcher among the owner's
 * cars in use, and «Другая машина»; the value then also carries that car's
 * gearbox, colour and mileage. Another car chosen in the tool never touches
 * the garage by itself — a signed-in person gets a small «Добавить в гараж»
 * (POST /api/me/car, mode=tool: the same model refines that car, another one
 * is added beside the others, while there is room).
 *
 * Why the list is in the flow and not a floating menu: an absolutely placed
 * menu is clipped by any overflow:hidden ancestor (CLAUDE.md, menus → popover);
 * a combobox list that pushes the content down cannot be clipped and needs no
 * positioning code. The page never scrolls by itself; only the list scrolls
 * inside, and the active option is brought into view with block:'nearest'.
 */

import { createCatalog, nameIn, yearsLabel, engineLabel, fold, startTier } from './catalog.js';
import { factsOf, choiceFor, yearOptions, yearSpan } from './car-facts.js';
import { choosePrefill, toPick, extrasOf, whenProfileCar } from './profile-car.js';
import { garageChoices, garageMatch, carLabel, hasRoom, garageCall, firstError, MAX_ACTIVE, logbookHere } from './garage.js';

const STORE_NS = 'car-catalog';
const STORE_KEY = 'last';
const MAX_OPTIONS = 12;

let catalogPromise = null;
let catalog = null;

function loadCatalog(api) {
    if (catalogPromise) return catalogPromise;
    catalogPromise = fetch(api || '/api/car-catalog', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then((r) => { if (!r.ok) throw new Error('car-catalog HTTP ' + r.status); return r.json(); })
        .then((j) => (catalog = createCatalog(j)))
        .catch((e) => { catalogPromise = null; throw e; });
    return catalogPromise;
}

const ae = (ev, d) => { try { window.__ae && window.__ae(ev, d || {}); } catch (e) { /* telemetry never breaks the picker */ } };
const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
const settings = () => window.__toolSettings || null;

function i18n() {
    const el = document.getElementById('car-picker-i18n');
    if (!el) return {};
    try { return JSON.parse(el.textContent || '{}'); } catch (e) { return {}; }
}

let I18N = null;
function t(k, sub) {
    if (!I18N) I18N = i18n();
    let s = I18N[k] != null ? I18N[k] : k;
    if (sub) for (const p in sub) s = String(s).split(':' + p).join(sub[p]);
    return s;
}

async function postSuggestion(payload) {
    const token = (document.querySelector('meta[name="csrf-token"]') || {}).content || '';
    const send = (tk) => fetch('/api/tool-suggestion', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': tk, Accept: 'application/json' },
        body: JSON.stringify(payload),
    });
    let r = await send(token);
    if (r.status === 419 && window.__refreshCsrf) r = await send(await window.__refreshCsrf());
    return r;
}

/* ───────────────────────────── one picker ─────────────────────────────── */

function mount(root) {
    if (!root || root.carPicker) return root && root.carPicker;
    const $ = (sel) => root.querySelector(sel);
    const ui = {
        q: $('.cp-q'), clear: $('.cp-clear'), mq: $('.cp-mq'), mclear: $('.cp-mclear'), list: $('.cp-list'), status: $('.cp-status'),
        fuelWrap: $('.cp-fuel-wrap'), fuels: $('.cp-fuels'), engFact: $('.cp-eng-fact'), engFactText: $('.cp-eng-fact-t'), engChange: $('.cp-eng-change'),
        vinqOpen: $('.cp-vinq-open'), vinq: $('.cp-vinq'), vinqIn: $('.cp-vinq-in'), vinqBtn: $('.cp-vinq-btn'), vinqMsg: $('.cp-vinq-msg'),
        more: $('.cp-more'), gen: $('.cp-gen'), year: $('.cp-year'), eng: $('.cp-eng'),
        genWrap: $('.cp-gen-wrap'), yearWrap: $('.cp-year-wrap'), engWrap: $('.cp-eng-wrap'),
        note: $('.cp-note'), cover: $('.cp-cover'), coverText: $('.cp-cover-text'), coverBtn: $('.cp-cover-btn'),
        missOpen: $('.cp-missing-open'), miss: $('.cp-missing'),
        mMake: $('.cp-m-make'), mModel: $('.cp-m-model'), mYear: $('.cp-m-year'), mEngine: $('.cp-m-engine'),
        mVin: $('.cp-m-vin'), mVinBtn: $('.cp-m-vin-btn'), mVinMsg: $('.cp-m-vin-msg'),
        mFound: $('.cp-m-found'), mFoundList: $('.cp-m-found-list'), mSend: $('.cp-m-send'), mAnyway: $('.cp-m-anyway'),
        mMsg: $('.cp-m-msg'), mDone: $('.cp-m-done'), mCancel: $('.cp-m-cancel'),
        prof: $('.cp-prof'), profOther: $('.cp-prof-other'), garage: $('.cp-garage'), garageOne: $('.cp-garage-one'),
        psave: $('.cp-psave'), psaveBtn: $('.cp-psave-btn'), psaveNow: $('.cp-psave-now'),
    };
    const opt = {
        slug: root.dataset.slug || '',
        api: root.dataset.api || '/api/car-catalog',
        steps: root.dataset.steps || 'engine',
        remember: root.dataset.remember !== '0',
        freeText: root.dataset.freeText === '1',
        wmi: root.dataset.wmi || '',
        locale: window.__LOCALE || 'en',
        profile: root.dataset.profile === '1',
        meCar: root.dataset.meCar || '/api/me/car',
    };
    const state = { makeId: null, modelId: null, generationId: null, engineId: null, engineText: null, year: null, text: '', touched: false,
        /* fuel the person picked among several (filters the engines); a year a VIN said before the model was chosen;
           «изменить» on an engine the data set by itself */
        fuel: null, pendingYear: null, engineOpen: false };
    /* Where the car on screen came from when nobody chose it here: 'browser'
       (the last car of any car tool) or 'profile'. A choice on this page — a
       click, typing, the page's own select() — sets `touched`, and from then on
       neither source may replace it. */
    let source = null;
    let rememberedAtMount = null;
    let profileCar = null;
    /* The owner's garage (./garage.js) and the car of it this page switched to. `garage` — the
       account's cars + the browser logbook's (`browser: true`, id 'l:…'); only the account's
       count for room and are ever posted. */
    let garage = [];
    let acctCars = [];
    let lbCars = [];
    const mergeCars = () => { garage = forThisTool(acctCars.concat(lbCars.filter((c) => !acctCars.some((a) => a && a.id === c.acct)))); };
    /* a tool of one country's law offers only the cars registered there (or not said): /transport-tax — Russian
       cars (docs/why/car-logbook-geo.md); an Australian car would get a Russian tax */
    const PLACE_ONLY = { 'transport-tax': 'ru' }[opt.slug] || null;
    const forThisTool = (cars) => (PLACE_ONLY ? (cars || []).filter((c) => !c.plate_country || c.plate_country === PLACE_ONLY) : cars);
    /* the car that fills an empty page obeys the same rule: an Australian main car must not
       prefill /transport-tax — the first car of the garage that may does (main first) */
    const prefillOf = (car, cars) => (!PLACE_ONLY || !car || !car.plate_country || car.plate_country === PLACE_ONLY ? car : (garageChoices(cars)[0] || null));
    let garagePick = null;
    let loggedIn = false;
    let options = [];
    let active = -1;
    let coverage = null;
    let verdict = { shown: [], clicked: [] };
    let restoredLabels = null;

    const cat = () => catalog;
    const make = () => (catalog && state.makeId ? catalog.make(state.makeId) : null);
    const model = () => (catalog && state.modelId ? catalog.model(state.modelId) : null);

    function setStatus(msg) { if (ui.status) ui.status.textContent = msg || ''; }

    function value() {
        const mk = make(), md = model();
        const g = catalog && state.generationId ? catalog.generation(state.generationId) : null;
        const e = catalog && state.engineId ? catalog.engine(state.engineId) : null;
        const labels = restoredLabels && !catalog ? Object.assign({}, restoredLabels) : {
            make: mk ? nameIn(mk, opt.locale) : null,
            model: md ? nameIn(md, opt.locale) : null,
            generation: g ? genLabel(g) : null,
            engine: e ? engineLabel(e, t) : (state.engineText || null),
        };
        const label = [labels.make, labels.model].filter(Boolean).join(' ')
            + (state.year ? ', ' + state.year : (labels.generation ? ', ' + labels.generation : ''))
            + (labels.engine ? ', ' + labels.engine : '');
        const v = {
            makeId: state.makeId, modelId: state.modelId, generationId: state.generationId,
            engineId: state.engineId, engineText: state.engineText, year: state.year, community: !!(md && md.own),
            labels, label: label.trim(), text: state.text, source,
            bodies: [], fuels: [], fuel: null,
        };
        /* what the catalogue knows of this car (./car-facts.js) — the forms ask the body
           with it, a tool may use the fuel; empty lists = unknown (ask) */
        if (catalog && md) {
            const f = factsOf(catalog, v);
            v.bodies = f.bodies;
            v.fuels = f.fuels;
            v.fuel = (e && e.fuel) || (f.fuels.length === 1 ? f.fuels[0] : (state.fuel && f.fuels.includes(state.fuel) ? state.fuel : null));
        }
        /* The owner's own car: what only the garage knows (gearbox, colour,
           mileage) rides along, so a tool that needs it does not ask again. */
        const gc = garageMatch(v, garage, garagePick);
        if (gc) Object.assign(v, extrasOf(gc), { profileCar: true, garageId: gc.id });
        return v;
    }

    function emit(extra) {
        const v = Object.assign(value(), extra || {});
        root.carPickerValue = v;
        try { root.dispatchEvent(new CustomEvent('car-picker:change', { detail: v, bubbles: true })); } catch (e) { /* old engines */ }
        renderCoverage(v);
        renderProfile(v);
        return v;
    }

    function remember() {
        if (!opt.remember || !state.modelId) return;
        const s = settings();
        if (!s) return;
        const v = value();
        s.save(STORE_NS, STORE_KEY, {
            makeId: v.makeId, modelId: v.modelId, generationId: v.generationId, engineId: v.engineId,
            year: v.year, labels: v.labels, at: Date.now(),
        });
    }

    function genLabel(g) {
        const y = yearsLabel(g.years, t);
        return [y, g.name].filter(Boolean).join(' · ') || g.id;
    }

    /* ── the two comboboxes: «Марка» and «Модель» (v3) ─────────────────── */

    /* Which field the one list below them serves now: 'make' | 'model'. */
    let field = 'make';
    const input = (f) => (f === 'model' ? ui.mq : ui.q);

    function openList(open, f) {
        if (f) field = f;
        ui.list.hidden = !open;
        ui.q.setAttribute('aria-expanded', open && field === 'make' ? 'true' : 'false');
        ui.mq.setAttribute('aria-expanded', open && field === 'model' ? 'true' : 'false');
        if (!open) {
            active = -1;
            ui.q.removeAttribute('aria-activedescendant');
            ui.mq.removeAttribute('aria-activedescendant');
        }
    }

    function optionHtml(o, i) {
        const c = catalog;
        const mk = c.make(o.makeId);
        let main, meta = '';
        if (o.kind === 'make') {
            main = esc(nameIn(mk, opt.locale));
            if (o.recent) meta = t('recent');
        } else {
            const md = c.model(o.modelId);
            /* in the model field the make is already in its own field */
            main = esc((o.bare ? '' : nameIn(mk, opt.locale) + ' ') + nameIn(md, opt.locale));
            /* the years beside a model — its OWN years only: the generations are the maintenance
               data's and incomplete (Granta read «с 2018»), the same rule as the year list (./car-facts.js) */
            const span = yearSpan(md);
            if (md.own) meta = t('community', { n: md.c || 1 });
            else if (span) meta = yearsLabel(span, t);
        }
        return (o.divider ? '<li class="cp-div" role="presentation">' + esc(o.divider) + '</li>' : '')
            + '<li role="option" class="cp-opt' + (o.kind === 'make' ? ' cp-opt--make' : '') + '" id="' + root.id + '-o' + i + '" data-i="' + i + '" aria-selected="false">'
            + '<span class="cp-opt-main">' + main + '</span>' + (meta ? '<span class="cp-opt-meta">' + esc(meta) + '</span>' : '') + '</li>';
    }

    /* the field shows a chosen value (not something being typed) */
    function fieldHoldsValue(f) {
        if (f === 'model') return !!state.modelId && !!model() && ui.mq.value === nameIn(model(), opt.locale);
        return !!state.makeId && !!make() && ui.q.value === nameIn(make(), opt.locale);
    }

    function renderList() {
        const inp = input(field);
        if (!options.length) {
            ui.list.innerHTML = '<li class="cp-empty" role="presentation">' + esc(t('no_results')) + '</li>';
            openList(true);
            setStatus(t('no_results'));
            return;
        }
        ui.list.innerHTML = options.map(optionHtml).join('');
        const mk = make();
        ui.list.setAttribute('aria-label', field === 'model' && mk ? t('models_of', { make: nameIn(mk, opt.locale) }) : t('listbox_label'));
        openList(true);
        /* typing: the best match is highlighted, Enter takes it; a list opened on a chosen
           value marks that value; an empty field highlights nothing */
        let at = -1;
        if (fieldHoldsValue(field)) at = options.findIndex((o) => (field === 'model' ? o.modelId === state.modelId : o.kind === 'make' && o.makeId === state.makeId && !o.recent));
        else if (String(inp.value || '').trim()) at = 0;
        setActive(at);
        setStatus(t('n_options', { n: options.length }));
    }

    function setActive(i) {
        const inp = input(field);
        const items = ui.list.querySelectorAll('.cp-opt');
        items.forEach((li) => li.setAttribute('aria-selected', 'false'));
        active = i;
        if (i < 0 || !items[i]) { inp.removeAttribute('aria-activedescendant'); return; }
        items[i].setAttribute('aria-selected', 'true');
        inp.setAttribute('aria-activedescendant', items[i].id);
        items[i].scrollIntoView({ block: 'nearest' });
    }

    /* The make the browser chose last (any car tool) — offered first on an empty field. */
    function recentMake() {
        const s = settings();
        const last = s ? s.load(STORE_NS, STORE_KEY, null) : null;
        return last && last.makeId && catalog && catalog.make(last.makeId) ? last.makeId : null;
    }

    /* What the list holds for a field and its text (catalog.js keeps the order rules):
       make, empty  — «недавно», «Популярные» (the market's makes), «Все марки» A–Z;
       make, typed  — the makes it fits (an exact name first, then the ones it starts, A–Z),
                      then models it names outright («гелик», «kia rio», «gla») — those set both fields;
       model, empty — every model of the make A–Z; typed — the ones it fits; none — other cars. */
    function computeOptions(f, text) {
        const c = catalog;
        const q = String(text || '').trim();
        const asOpt = (r, extra) => Object.assign({ kind: r.kind, makeId: r.makeId, modelId: r.modelId, generations: r.generations, engines: r.engines, year: r.year }, extra || {});
        if (f === 'model') {
            if (!state.makeId) return [];
            const own = c.filterModels(state.makeId, q).map((md) => ({ kind: 'model', makeId: state.makeId, modelId: md.id, bare: true }));
            if (own.length || !q) {
                /* a year or an engine typed with the model («rio 2015», «x50 1.5t») still narrows it */
                if (q) {
                    const hits = new Map(c.search(q, { makeId: state.makeId, kind: 'model', limit: 80 }).map((r) => [r.modelId, r]));
                    own.forEach((o) => { const r = hits.get(o.modelId); if (r) Object.assign(o, { generations: r.generations, engines: r.engines, year: r.year }); });
                }
                return own;
            }
            const other = c.search(q, { limit: MAX_OPTIONS }).filter((r) => r.kind === 'model');
            return other.map((r, i) => asOpt(r, i === 0 ? { divider: t('other_makes') } : null));
        }
        if (!q) {
            const rec = recentMake();
            const out = [];
            if (rec) out.push({ kind: 'make', makeId: rec, recent: true });
            c.popularMakes(opt.locale, 8).filter((mk) => mk.id !== rec)
                .forEach((mk, i) => out.push({ kind: 'make', makeId: mk.id, divider: i === 0 ? t('popular') : null }));
            c.makesAlpha().forEach((mk, i) => out.push({ kind: 'make', makeId: mk.id, divider: i === 0 ? t('all_makes') : null }));
            return out;
        }
        /* a make the text starts (or is) leads; a model it names comes next; a make it only
           resembles («гелик» ≈ Geely) goes after the model it names exactly (G-Class) */
        const all = c.filterMakes(q);
        const near = all.filter((mk) => startTier(mk, q) === 2);
        const mks = all.filter((mk) => startTier(mk, q) < 2).map((mk) => ({ kind: 'make', makeId: mk.id }));
        /* models here only when they are what the words say: no make starts so, or two words
           («kia rio»), or the exact name of a model or its nickname («gla», «гелик») — «ме» is
           Mercedes-Benz, not Mégane and Meriva */
        const several = q.split(/\s+/).filter(Boolean).length > 1;
        const mds = c.search(q, { kind: 'model', limit: 6 })
            .filter((r) => !mks.length || several || startTier(c.model(r.modelId), q) === 0)
            .map((r) => asOpt(r));
        if (mks.length && mds.length) mds[0].divider = t('models_cross');
        const rest = near.map((mk) => ({ kind: 'make', makeId: mk.id }));
        if (rest.length && (mks.length || mds.length)) rest[0].divider = t('all_makes');
        return mks.concat(mds, rest);
    }

    let typingTimer = 0;
    function onInput(f) {
        field = f;
        state.touched = true;
        source = null;
        if (f === 'make') {
            /* typing over a chosen make: another make — the model goes with it */
            if (state.makeId || state.modelId) {
                const typed = ui.q.value;
                resetCar();
                ui.q.value = typed;
                renderFields();
                renderMore();
                emit();
            }
            state.text = ui.q.value;
        } else {
            if (state.modelId) {
                /* typing over a chosen model: another model of the same make */
                state.modelId = null; state.generationId = null; state.engineId = null; state.engineText = null; state.year = null;
                state.fuel = null; state.engineOpen = false;
                restoredLabels = null;
                renderMore();
                emit();
            }
            const mk = make();
            state.text = ((mk ? nameIn(mk, opt.locale) : ui.q.value) + ' ' + ui.mq.value).trim();
        }
        syncClears();
        clearTimeout(typingTimer);
        typingTimer = setTimeout(async () => {
            if (!catalog) {
                setStatus(t('loading'));
                /* a slow line: say it in the list itself, not only to the screen reader */
                ui.list.innerHTML = '<li class="cp-empty" role="presentation">' + esc(t('loading')) + '</li>';
                openList(true, f);
                try { await loadCatalog(opt.api); } catch (e) { setStatus(t('load_failed')); ae('tool_error', { action: 'car_catalog_load', slug: opt.slug }); return; }
            }
            if (field !== f) return;
            options = computeOptions(f, input(f).value);
            renderList();
            takePendingEnter();
        }, 60);
    }

    /* Enter pressed while the catalogue was still loading (a slow line: «kia» ⏎ before the
       list existed) is not lost: once the list is there, the top match is taken — if the
       field still says what it said at that Enter. */
    let pendingEnter = null;
    function takePendingEnter() {
        const p = pendingEnter;
        pendingEnter = null;
        if (p == null || input(field).value !== p || ui.list.hidden || !options.length) return;
        choose(options[active >= 0 && options[active] ? active : 0]);
    }

    async function onFocus(f) {
        field = f;
        if (!catalog) {
            try { await loadCatalog(opt.api); } catch (e) { setStatus(t('load_failed')); return; }
            if (restoredLabels) hydrate();
        }
        if (document.activeElement !== input(f)) return;
        if (f === 'model' && !state.makeId) return;
        /* a field holding its value opens on the whole list (the value marked), not on the value alone */
        options = computeOptions(f, fieldHoldsValue(f) ? '' : input(f).value);
        renderList();
        takePendingEnter();
    }

    /* how: 'user' (a click, Enter), 'select' (the page's own select()),
       'hydrate' (a form filling itself in — no telemetry), 'profile' (the
       profile car arrived — not a choice: it neither marks the page as chosen
       nor goes into the browser's memory). */
    function choose(o, how) {
        if (!o) return;
        how = how || 'user';
        if (how !== 'profile') { state.touched = true; source = null; }
        if (o.kind === 'make') {
            const same = state.makeId === o.makeId && !!state.modelId;
            state.makeId = o.makeId;
            if (!same) {
                state.modelId = null;
                state.generationId = null; state.engineId = null; state.engineText = null;
                state.year = state.pendingYear || null; state.fuel = null; state.engineOpen = false;
                ui.mq.value = '';
            }
            restoredLabels = null;
            const mk = catalog.make(o.makeId);
            ui.q.value = nameIn(mk, opt.locale);
            state.text = nameIn(mk, opt.locale) + (same ? ' ' + nameIn(model(), opt.locale) : '');
            openList(false);
            renderFields();
            renderMore();
            emit(how === 'profile' ? { restored: true } : undefined);
            if (how === 'user' && !same) {
                /* the next question at once: the model field with its whole list */
                ui.mq.focus();
                if (document.activeElement === ui.mq) { field = 'model'; options = computeOptions('model', ''); renderList(); }
            }
            return;
        }
        state.makeId = o.makeId;
        state.modelId = o.modelId;
        state.generationId = null; state.engineId = null; state.engineText = null; state.fuel = null; state.engineOpen = false;
        state.year = o.year || state.pendingYear || null;
        state.pendingYear = null;
        restoredLabels = null;
        const md = catalog.model(o.modelId);
        const gens = md.generations || [];
        if (o.generations && o.generations.length === 1) state.generationId = o.generations[0];
        else if (gens.length === 1) state.generationId = gens[0].id;
        else if (state.year) { const g = catalog.generationsOf(md.id, state.year); if (g.length === 1) state.generationId = g[0].id; }
        if (o.engines && o.engines.length === 1) state.engineId = o.engines[0];
        autoEngine();
        ui.q.value = nameIn(catalog.make(o.makeId), opt.locale);
        ui.mq.value = nameIn(md, opt.locale);
        state.text = ui.q.value + ' ' + ui.mq.value;
        openList(false);
        renderFields();
        renderMore();
        setStatus(t('chosen', { car: state.text }));
        const v = emit(how === 'profile' ? { restored: true } : undefined);
        if (how === 'profile') return;
        remember();
        if (how !== 'hydrate') ae('feature_use', { slug: opt.slug, action: 'car_pick', make: v.makeId, model: v.modelId, own: v.community ? 1 : 0, f: o.bare ? 'model' : 'make' });
    }

    /* ── the two fields show the state ──────────────────────────────────── */

    function syncClears() {
        ui.clear.hidden = !(ui.q.value || state.makeId);
        ui.mclear.hidden = ui.mq.disabled || !(ui.mq.value || state.modelId);
    }

    /* «Марка» shows the chosen make, «Модель» the chosen model; the model field opens with a make. */
    function renderFields() {
        const mk = make();
        const mkName = mk ? nameIn(mk, opt.locale) : (restoredLabels && state.makeId ? restoredLabels.make || '' : '');
        const md = model();
        const mdName = md ? nameIn(md, opt.locale) : (restoredLabels && state.modelId ? restoredLabels.model || '' : '');
        if (state.makeId && mkName) ui.q.value = mkName;
        if (state.modelId && mdName) ui.mq.value = mdName;
        ui.mq.disabled = !state.makeId;
        ui.mq.placeholder = state.makeId ? t('ph_model') : t('model_wait');
        if (!state.makeId) ui.mq.value = '';
        syncClears();
    }

    function resetCar() {
        state.makeId = state.modelId = state.generationId = state.engineId = state.engineText = state.year = null;
        state.fuel = null; state.engineOpen = false;
        restoredLabels = null;
        ui.mq.value = '';
    }

    /* ── generation / year / engine ─────────────────────────────────────── */

    function fillSelect(sel, items, current) {
        sel.innerHTML = '<option value="">' + esc(t('unknown')) + '</option>'
            + items.map((it) => '<option value="' + esc(it.value) + '"' + (String(it.value) === String(current || '') ? ' selected' : '') + '>' + esc(it.label) + '</option>').join('');
    }

    /* The engines this car may have — the generation's, filtered by the fuel the person picked. */
    function engineChoices(md) {
        const all = catalog.enginesOf(md.id, state.generationId);
        return state.fuel ? all.filter((e) => !e.fuel || e.fuel === state.fuel) : all;
    }

    /* One engine known for this car → it is the engine (a fact, «изменить»); several → asked, never guessed. */
    function autoEngine() {
        const md = model();
        if (!md || state.engineId || state.engineText) return;
        const engs = engineChoices(md);
        if (engs.length === 1) state.engineId = engs[0].id;
    }

    function renderMore() {
        const md = model();
        if (!md || opt.steps === 'model') { ui.more.hidden = true; renderNote(); return; }
        const facts = factsOf(catalog, { modelId: md.id, generationId: state.generationId, year: state.year });
        // the YEAR first — the model's production years when known (./car-facts.js)
        const ys = yearOptions(facts.years).map((y) => ({ value: y, label: String(y) }));
        /* a year saved before (or said by a VIN) outside the known span stays visible as itself */
        if (state.year && !ys.some((o) => o.value === state.year)) ys.unshift({ value: state.year, label: String(state.year) });
        fillSelect(ui.year, ys, state.year);
        ui.yearWrap.hidden = false;
        // the generation only when the year does not say it (or the tool asks generations, not years)
        const all = catalog.generationsOf(md.id);
        const forYear = state.year ? catalog.generationsOf(md.id, state.year) : [];
        const ambiguous = all.length > 1 && (opt.steps === 'generation' ? true : (state.year ? forYear.length > 1 : false));
        const gens = state.year && forYear.length > 1 ? forYear : all;
        ui.genWrap.hidden = !(ambiguous || (state.generationId && all.length > 1 && !state.year));
        if (!ui.genWrap.hidden) fillSelect(ui.gen, gens.map((g) => ({ value: g.id, label: genLabel(g) })), state.generationId);
        if (opt.steps === 'generation' && all.length) ui.yearWrap.hidden = true;
        // FUEL among several (filters the engines) — only when it narrows a real choice
        const engsAll = catalog.enginesOf(md.id, state.generationId);
        const fuelChoice = choiceFor(facts.fuels, state.fuel, []);
        const fuelMatters = fuelChoice.mode === 'chips' && engsAll.length > 1 && opt.steps === 'engine';
        if (ui.fuelWrap) {
            ui.fuelWrap.hidden = !fuelMatters;
            if (fuelMatters) {
                if (!state.fuel) state.fuel = fuelChoice.value;
                ui.fuels.innerHTML = fuelChoice.options.map((f) => '<button type="button" class="cp-fchip" data-fuel="' + esc(f) + '" aria-pressed="'
                    + (f === state.fuel) + '">' + esc(t('fuel_' + f)) + '</button>').join('');
            }
        }
        // the ENGINE: a fact when one is known, else the list (never a guess among several)
        const engs = engineChoices(md);
        const items = engs.map((e) => ({ value: e.id, label: engineLabel(e, t) }));
        if (!items.length && md.eng) md.eng.forEach((w) => items.push({ value: 'txt:' + w, label: w }));
        const fact = opt.steps === 'engine' && engs.length === 1 && state.engineId === engs[0].id && !state.engineOpen;
        if (ui.engFact) {
            ui.engFact.hidden = !fact;
            if (fact) ui.engFactText.textContent = engineLabel(engs[0], t);
        }
        ui.engWrap.hidden = opt.steps !== 'engine' || !items.length || fact;
        fillSelect(ui.eng, items, state.engineId || (state.engineText ? 'txt:' + state.engineText : ''));
        ui.more.hidden = ui.genWrap.hidden && ui.yearWrap.hidden && ui.engWrap.hidden && (!ui.engFact || ui.engFact.hidden) && (!ui.fuelWrap || ui.fuelWrap.hidden);
        renderNote();
    }

    function renderNote() {
        const md = model();
        if (md && md.own) {
            ui.note.textContent = t('community_note', { n: md.c || 1 });
            ui.note.hidden = false;
        } else {
            ui.note.hidden = true;
        }
    }

    function hydrate() {
        // a restored car: the catalogue just arrived — fill the selects for real
        if (!catalog || !state.modelId) return;
        if (!catalog.model(state.modelId)) {
            // the model left the catalogue (a dismissed owner row): start over quietly
            clear(true);
            return;
        }
        restoredLabels = null;
        if (state.generationId && !catalog.generation(state.generationId)) state.generationId = null;
        if (state.engineId && !catalog.engine(state.engineId)) state.engineId = null;
        renderFields();
        renderMore();
    }

    function clear(silent) {
        state.makeId = state.modelId = state.generationId = state.engineId = state.engineText = state.year = null;
        state.fuel = null; state.pendingYear = null; state.engineOpen = false;
        state.text = '';
        restoredLabels = null;
        source = null;
        if (!silent) state.touched = true;
        ui.q.value = '';
        ui.mq.value = '';
        openList(false);
        renderFields();
        renderMore();
        if (!silent) {
            const s = settings();
            if (s && opt.remember) s.save(STORE_NS, STORE_KEY, null);
            emit();
        }
    }

    /* ── coverage: «this tool has no data for your car yet» ─────────────── */

    function askedKey(v) { return 'asked:' + opt.slug + ':' + (v.generationId || v.modelId); }

    function renderCoverage(v) {
        if (!ui.cover) return;
        if (!coverage || !v.modelId) { ui.cover.hidden = true; return; }
        let ok = null;
        try { ok = coverage(v); } catch (e) { ok = null; }
        if (ok !== false) { ui.cover.hidden = true; return; }
        const s = settings();
        const asked = s ? s.load(STORE_NS, askedKey(v), null) : null;
        ui.coverText.textContent = t(asked ? 'coverage_counted' : 'coverage_missing');
        ui.coverBtn.hidden = !!asked;
        ui.coverBtn.disabled = false;
        ui.cover.hidden = false;
    }

    async function askCoverage() {
        const v = value();
        if (!v.modelId) return;
        ui.coverBtn.disabled = true;
        try {
            const r = await postSuggestion({
                tool_slug: 'car-coverage',
                data: { tool: opt.slug, make: v.makeId || '', model: v.modelId || '', generation: v.generationId || '', engine: v.engineId || '', label: v.label },
                locale: opt.locale,
            });
            if (!r.ok) throw new Error('HTTP ' + r.status);
            const s = settings();
            if (s) s.save(STORE_NS, askedKey(v), Date.now());
            ae('feature_use', { slug: opt.slug, action: 'car_coverage_ask', model: v.modelId });
            renderCoverage(v);
        } catch (e) {
            ui.coverBtn.disabled = false;
            window.__toast && window.__toast(t('send_failed'), 'error');
        }
    }

    /* ── «Нет моей машины» ──────────────────────────────────────────────── */

    function openMissing() {
        ui.miss.hidden = false;
        ui.missOpen.setAttribute('aria-expanded', 'true');
        ui.mDone.hidden = true;
        ui.mFound.hidden = true;
        ui.mMsg.textContent = '';
        const mk = make();
        const typed = String(ui.q.value || '').trim();
        const typedModel = String(ui.mq.value || '').trim();
        if (mk) {
            ui.mMake.value = nameIn(mk, opt.locale);
            if (!state.modelId && typedModel) ui.mModel.value = typedModel;
        } else if (typed && !ui.mMake.value) {
            const parts = typed.split(/\s+/);
            ui.mMake.value = parts[0] || '';
            ui.mModel.value = parts.slice(1).join(' ');
        }
        if (state.year && !ui.mYear.value) ui.mYear.value = state.year;
        loadCatalog(opt.api).catch(() => {});
        openList(false);
        (ui.mMake.value ? ui.mModel : ui.mMake).focus();
        ae('feature_use', { slug: opt.slug, action: 'car_missing_open' });
    }

    function closeMissing() {
        ui.miss.hidden = true;
        ui.missOpen.setAttribute('aria-expanded', 'false');
        ui.missOpen.focus();
    }

    async function fillFromVin() {
        const raw = String(ui.mVin.value || '').trim();
        ui.mVinMsg.textContent = '';
        if (!raw) return;
        try {
            if (!catalog) { ui.mVinMsg.textContent = t('loading'); await loadCatalog(opt.api); }
            const vin = await import('../vin/vin.js');
            if (opt.wmi && typeof vin.useWmiData === 'function' && typeof vin.hasWmiData === 'function' && !vin.hasWmiData()) {
                try {
                    const r = await fetch(opt.wmi, { credentials: 'same-origin' });
                    if (r.ok) vin.useWmiData(await r.json());
                } catch (e) { /* the core table still names the big makers */ }
            }
            const res = catalog.fromVin(raw, vin.decode);
            if (!res.valid) { ui.mVinMsg.textContent = t('vin_bad'); return; }
            const mk = res.makeId ? catalog.make(res.makeId) : null;
            if (mk) ui.mMake.value = nameIn(mk, opt.locale);
            // a year this button wrote earlier must not outlive the VIN it came from
            if (res.year) { ui.mYear.value = res.year; ui.mYear.dataset.fromVin = '1'; }
            else if (ui.mYear.dataset.fromVin) { ui.mYear.value = ''; delete ui.mYear.dataset.fromVin; }
            ui.mVin.value = res.vin;
            ui.mVinMsg.textContent = mk
                ? t(res.year ? 'vin_found_year' : 'vin_found', { make: nameIn(mk, opt.locale), year: res.year || '' })
                : t('vin_no_make');
            ae('feature_use', { slug: opt.slug, action: 'car_vin_fill', make: res.makeId || '', year: res.year ? 1 : 0 });
        } catch (e) {
            ui.mVinMsg.textContent = t('vin_bad');
        }
    }

    /* «По VIN»: what the VIN says for certain — the make (one of ours), the model year
       when the decoder calls it reliable — and the person picks the model. */
    async function vinQuick() {
        const raw = String(ui.vinqIn.value || '').trim();
        ui.vinqMsg.textContent = '';
        if (!raw) return;
        try {
            if (!catalog) { ui.vinqMsg.textContent = t('loading'); await loadCatalog(opt.api); }
            const vin = await import('../vin/vin.js');
            if (opt.wmi && typeof vin.useWmiData === 'function' && typeof vin.hasWmiData === 'function' && !vin.hasWmiData()) {
                try { const r = await fetch(opt.wmi, { credentials: 'same-origin' }); if (r.ok) vin.useWmiData(await r.json()); } catch (e) { /* the core table still names the big makers */ }
            }
            const res = catalog.fromVin(raw, vin.decode);
            if (!res.valid) { ui.vinqMsg.textContent = t('vin_bad'); return; }
            const mk = res.makeId ? catalog.make(res.makeId) : null;
            if (!mk) { ui.vinqMsg.textContent = t('vin_no_make'); return; }
            state.pendingYear = res.year || null;
            if (state.makeId !== res.makeId || state.modelId) choose({ kind: 'make', makeId: res.makeId });
            else state.year = res.year || state.year;
            ui.vinqMsg.textContent = t(res.year ? 'vin_found_year' : 'vin_found', { make: nameIn(mk, opt.locale), year: res.year || '' }) + ' ' + t('vin_pick_model');
            root.dispatchEvent(new CustomEvent('car-picker:vin', { detail: { vin: res.vin, makeId: res.makeId, year: res.year || null }, bubbles: true }));
            ae('feature_use', { slug: opt.slug, action: 'car_vin_quick', make: res.makeId, year: res.year ? 1 : 0 });
        } catch (e) {
            ui.vinqMsg.textContent = t('vin_bad');
        }
    }

    function foundFor(makeText, modelText, year) {
        if (!catalog) return [];
        const q = [makeText, modelText, year || ''].join(' ').trim();
        return catalog.search(q, { limit: 6, typing: false }).filter((r) => r.kind === 'model' && r.score >= 2).slice(0, 4);
    }

    async function sendMissing(force) {
        const makeText = String(ui.mMake.value || '').trim();
        const modelText = String(ui.mModel.value || '').trim();
        const year = String(ui.mYear.value || '').trim();
        if (!makeText || !modelText) { ui.mMsg.textContent = t('need_make_model'); return; }
        if (!catalog) { try { await loadCatalog(opt.api); } catch (e) { /* send without the check */ } }
        /* The form answers first: if the catalogue already has this car, the
           visitor sees it as a choice and the request is not needed. */
        const found = foundFor(makeText, modelText, year);
        if (!force && found.length) {
            verdict.shown = found.map((r) => r.modelId);
            ui.mFoundList.innerHTML = found.map((r) => {
                const md = catalog.model(r.modelId), mk = catalog.make(r.makeId);
                return '<button type="button" class="cp-chip" data-model="' + esc(r.modelId) + '">' + esc(nameIn(mk, opt.locale) + ' ' + nameIn(md, opt.locale)) + '</button>';
            }).join('');
            ui.mFound.hidden = false;
            ui.mFoundList.querySelector('button') && ui.mFoundList.querySelector('button').focus();
            return;
        }
        const a = verdict.shown.length ? 'chips' : (catalog ? 'no_match' : 'silent');
        const vinRaw = String(ui.mVin.value || '').toUpperCase().replace(/[\s\-_.]/g, '');
        const form = {
            v: 1, a, ids: verdict.shown.slice(0, 6), k: verdict.clicked.slice(0, 12), tt: 0, b: 0,
            q: (makeText + ' ' + modelText).slice(0, 60), key: fold(makeText + ' ' + modelText).replace(/[^\p{L}\p{N}]+/gu, ' ').trim().slice(0, 120), n: verdict.shown.length,
        };
        const mkId = catalog ? (catalog.search(makeText, { limit: 1, kind: 'make', typing: false })[0] || {}).makeId : '';
        ui.mSend.disabled = true;
        ui.mAnyway.disabled = true;
        try {
            const r = await postSuggestion({
                tool_slug: 'car-model',
                data: {
                    make: makeText, make_id: mkId || '', model: modelText, year,
                    engine: String(ui.mEngine.value || '').trim(),
                    vin: /^[A-HJ-NPR-Z0-9]{11,17}$/.test(vinRaw) ? vinRaw.slice(0, 11) : '',
                    tool: opt.slug, _form: JSON.stringify(form),
                },
                locale: opt.locale,
            });
            if (!r.ok) throw Object.assign(new Error('HTTP ' + r.status), { status: r.status });
            ui.mFound.hidden = true;
            ui.mDone.hidden = false;
            ui.mMsg.textContent = '';
            if (!state.modelId && opt.freeText) {
                ui.q.value = makeText + ' ' + modelText + (year ? ' ' + year : '');
                state.text = ui.q.value;
                syncClears();
                emit();
            }
            ae('feature_use', { slug: opt.slug, action: 'car_missing_sent', verdict: a, shown: verdict.shown.length, vin: vinRaw ? 1 : 0 });
        } catch (e) {
            ui.mMsg.textContent = t(e && e.status === 429 ? 'rate_limit' : 'send_failed');
        } finally {
            ui.mSend.disabled = false;
            ui.mAnyway.disabled = false;
        }
    }

    function pickFound(modelId) {
        verdict.clicked.push(modelId);
        const md = catalog.model(modelId);
        const mk = catalog.makeOf(modelId);
        if (!md || !mk) return;
        const year = parseInt(ui.mYear.value, 10) || null;
        const gens = year ? catalog.generationsOf(modelId, year).map((g) => g.id) : [];
        choose({ kind: 'model', makeId: mk.id, modelId, year, generations: gens });
        ui.miss.hidden = true;
        ui.missOpen.setAttribute('aria-expanded', 'false');
        ui.mq.focus();
        ae('feature_use', { slug: opt.slug, action: 'car_missing_answered', model: modelId });
    }

    /* ── the profile car ────────────────────────────────────────────────── */

    function pickModel(v, how) {
        const md = catalog.model(v.modelId), mk = catalog.makeOf(v.modelId);
        if (!md || !mk) return value();
        const gen = v.generationId && catalog.generation(v.generationId) ? [v.generationId] : undefined;
        const eng = v.engineId && catalog.enginesOf(md.id, gen ? gen[0] : null).some((e) => e.id === v.engineId) ? [v.engineId] : undefined;
        choose({ kind: 'model', makeId: mk.id, modelId: md.id, year: v.year || null, generations: gen, engines: eng }, how);
        if (v.engineText && !state.engineId && (md.eng || []).includes(v.engineText)) {
            state.engineText = v.engineText;
            renderMore();
            emit(how === 'profile' ? { restored: true } : undefined);
        }
        return value();
    }

    /* The profile car takes an empty page. Before the catalogue is here it is
       shown by its saved labels (like the browser's last car), and filled in
       for real in idle time. */
    function applyProfile(car) {
        const p = toPick(car);
        if (!p) return;
        source = 'profile';
        garagePick = car.id || null;
        if (catalog) {
            if (p.modelId) pickModel(p, 'profile');
            else if (catalog.make(p.makeId)) choose({ kind: 'make', makeId: p.makeId }, 'profile');
            return;
        }
        state.makeId = p.makeId;
        state.modelId = p.modelId;
        state.generationId = p.generationId;
        state.engineId = p.engineId;
        state.engineText = p.engineText;
        state.year = p.year;
        restoredLabels = p.labels;
        ui.q.value = p.labels.make || '';
        ui.mq.value = p.modelId ? (p.labels.model || '') : '';
        state.text = [p.labels.make, p.labels.model].filter(Boolean).join(' ');
        renderFields();
        emit({ restored: true });
        const idle = window.requestIdleCallback || ((f) => setTimeout(f, 300));
        idle(() => loadCatalog(opt.api).then(() => {
            if (state.touched || source !== 'profile') return;
            if (state.modelId) hydrate();
            else restoredLabels = null;
            emit({ restored: true });
        }).catch(() => {}));
    }

    function profileLabel(car) {
        return carLabel(car) + (car && car.year ? ', ' + car.year : '');
    }

    /* «Из вашего гаража: X ▾» whenever the owner has a car in use; the switcher
       lists them (main first). A car that is not in the garage shows the empty
       choice and, while there is room, «Добавить в гараж». */
    function renderProfile(v) {
        if (!opt.profile || !ui.prof) return;
        const list = garageChoices(garage).filter((c) => c.make_id);
        const gc = garageMatch(v, garage, garagePick);
        ui.prof.hidden = !list.length;
        if (list.length) {
            const many = list.length > 1 || !gc;
            if (ui.garage) {
                ui.garage.hidden = !many;
                const key = list.map((c) => c.id + ':' + profileLabel(c)).join('|') + '|' + (gc ? gc.id : '');
                if (ui.garage.dataset.key !== key) {
                    ui.garage.dataset.key = key;
                    ui.garage.innerHTML = (gc ? '' : '<option value="">' + esc(t('garage_pick')) + '</option>')
                        + list.map((c) => '<option value="' + esc(c.id) + '">' + esc(profileLabel(c)) + '</option>').join('');
                }
                ui.garage.value = gc ? gc.id : '';
            }
            if (ui.garageOne) {
                ui.garageOne.hidden = many;
                ui.garageOne.textContent = !many && gc ? profileLabel(gc) : '';
            }
            if (ui.profOther) ui.profOther.hidden = !gc;
        }
        const offer = loggedIn && !!v.modelId && !gc;
        ui.psave.hidden = !offer;
        if (offer) {
            const room = hasRoom(acctCars);
            ui.psaveBtn.hidden = !room;
            ui.psaveBtn.disabled = false;
            ui.psaveNow.textContent = room ? '' : t('garage_full', { max: MAX_ACTIVE });
        }
    }

    /* The switcher: another car of the garage — a choice of this page (it is
       remembered for the next car tool like any pick). */
    async function useGarageCar(id) {
        const car = garage.find((c) => c.id === id);
        if (!car) return;
        /* with the catalogue here the generation, year and engine fill for real */
        try { await loadCatalog(opt.api); } catch (e) { /* shown by its saved labels */ }
        applyProfile(car);
        state.touched = true;
        source = 'garage';
        garagePick = id;
        remember();
        emit();
        ae('feature_use', { slug: opt.slug, action: 'car_garage_switch', n: garageChoices(garage).length });
    }

    async function saveToProfile() {
        const v = value();
        if (!v.modelId) return;
        ui.psaveBtn.disabled = true;
        try {
            const j = await garageCall({
                mode: 'tool',
                car: { make_id: v.makeId, model_id: v.modelId, generation_id: v.generationId, engine_id: v.engineId, engine_text: v.engineText, year: v.year },
            }, opt.meCar);
            if (Array.isArray(j.cars)) { acctCars = j.cars; mergeCars(); }
            profileCar = j.car || profileCar;
            garagePick = j.id || null;
            source = 'garage';
            window.__toast && window.__toast(t('saved_to_garage'), 'success');
            ae('feature_use', { slug: opt.slug, action: 'car_profile_save', model: v.modelId });
            emit();
        } catch (e) {
            ui.psaveBtn.disabled = false;
            window.__toast && window.__toast(firstError(e) || t('profile_save_failed'), 'error');
        }
    }

    /* ── wiring ─────────────────────────────────────────────────────────── */

    function onKey(f, e) {
        const inp = input(f);
        const n = options.length;
        const mine = !ui.list.hidden && field === f;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (!mine) { onFocus(f); return; }
            if (n) setActive(active < n - 1 ? active + 1 : 0);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            if (mine && n) setActive(active > 0 ? active - 1 : n - 1);
        } else if (e.key === 'Enter') {
            if (!catalog && String(inp.value || '').trim()) { e.preventDefault(); field = f; pendingEnter = inp.value; }
            else if (mine && active >= 0 && options[active]) { e.preventDefault(); choose(options[active]); }
            else if (mine && options.length === 1) { e.preventDefault(); choose(options[0]); }
        } else if (e.key === 'Escape') {
            if (mine) { e.preventDefault(); openList(false); }
        } else if (e.key === 'Tab') {
            openList(false);
        } else if (e.key === 'Backspace' && f === 'model' && !ui.mq.value) {
            /* back from «Модель» to «Марка» — the make stays, its field takes the focus */
            e.preventDefault();
            openList(false);
            ui.q.focus();
            try { ui.q.select(); } catch (_) { /* old engine */ }
        }
    }
    ui.q.addEventListener('input', () => onInput('make'));
    ui.q.addEventListener('focus', () => onFocus('make'));
    ui.q.addEventListener('click', () => { if (ui.list.hidden || field !== 'make') onFocus('make'); });
    ui.q.addEventListener('keydown', (e) => onKey('make', e));
    ui.mq.addEventListener('input', () => onInput('model'));
    ui.mq.addEventListener('focus', () => onFocus('model'));
    ui.mq.addEventListener('click', () => { if (ui.list.hidden || field !== 'model') onFocus('model'); });
    ui.mq.addEventListener('keydown', (e) => onKey('model', e));
    const onBlur = () => {
        // a click on an option lands after blur: give it the moment it needs
        setTimeout(() => {
            if (!root.contains(document.activeElement) || (document.activeElement !== ui.q && document.activeElement !== ui.mq && !ui.list.contains(document.activeElement))) openList(false);
            /* a chosen value the person typed over and left: the field shows the value again */
            if (document.activeElement !== ui.q && state.makeId && make() && ui.q.value !== nameIn(make(), opt.locale) && !opt.freeText) renderFields();
            if (document.activeElement !== ui.mq && state.modelId && model() && ui.mq.value !== nameIn(model(), opt.locale)) renderFields();
            if (opt.freeText && !state.modelId) emit();
        }, 150);
    };
    ui.q.addEventListener('blur', onBlur);
    ui.mq.addEventListener('blur', onBlur);
    ui.list.addEventListener('mousedown', (e) => e.preventDefault());
    ui.list.addEventListener('click', (e) => {
        const li = e.target.closest('.cp-opt');
        if (li) choose(options[+li.dataset.i]);
    });
    ui.clear.addEventListener('click', () => { clear(false); ui.q.focus(); });
    ui.mclear.addEventListener('click', () => {
        state.touched = true; source = null;
        state.modelId = null; state.generationId = null; state.engineId = null; state.engineText = null; state.year = null;
        state.fuel = null; state.engineOpen = false; restoredLabels = null;
        ui.mq.value = '';
        state.text = make() ? nameIn(make(), opt.locale) : '';
        renderFields(); renderMore(); emit();
        ui.mq.focus();
    });
    ui.gen.addEventListener('change', () => {
        state.touched = true; source = null;
        state.generationId = ui.gen.value || null;
        if (state.engineId && state.generationId) {
            const ok = catalog.enginesOf(state.modelId, state.generationId).some((e) => e.id === state.engineId);
            if (!ok) state.engineId = null;
        }
        autoEngine();
        const g = state.generationId ? catalog.generation(state.generationId) : null;
        if (g && state.year && g.years && ((g.years[0] && state.year < g.years[0]) || (g.years[1] && state.year > g.years[1]))) state.year = null;
        renderMore(); emit(); remember();
    });
    ui.year.addEventListener('change', () => {
        state.touched = true; source = null;
        state.year = parseInt(ui.year.value, 10) || null;
        /* the year picks the generation; a generation the year does not belong to goes */
        if (state.year) {
            const gens = catalog.generationsOf(state.modelId, state.year);
            if (state.generationId && !gens.some((g) => g.id === state.generationId)) state.generationId = null;
            if (!state.generationId && gens.length === 1) state.generationId = gens[0].id;
            if (state.engineId && !catalog.enginesOf(state.modelId, state.generationId).some((e) => e.id === state.engineId)) state.engineId = null;
        }
        autoEngine();
        renderMore(); emit(); remember();
    });
    if (ui.fuels) ui.fuels.addEventListener('click', (e) => {
        const b = e.target.closest('[data-fuel]');
        if (!b) return;
        state.touched = true; source = null;
        state.fuel = b.dataset.fuel;
        const eng = state.engineId ? catalog.engine(state.engineId) : null;
        if (eng && eng.fuel && eng.fuel !== state.fuel) state.engineId = null;
        autoEngine();
        renderMore(); emit(); remember();
    });
    if (ui.engChange) ui.engChange.addEventListener('click', () => {
        state.engineOpen = true;
        renderMore();
        ui.eng.focus();
    });
    ui.eng.addEventListener('change', () => {
        state.touched = true; source = null;
        const v = ui.eng.value || '';
        state.engineId = v && !v.startsWith('txt:') ? v : null;
        state.engineText = v.startsWith('txt:') ? v.slice(4) : null;
        emit(); remember();
    });
    if (ui.coverBtn) ui.coverBtn.addEventListener('click', askCoverage);
    if (ui.vinqOpen) ui.vinqOpen.addEventListener('click', () => {
        const open = ui.vinq.hidden;
        ui.vinq.hidden = !open;
        ui.vinqOpen.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (open) { ui.vinqIn.focus(); loadCatalog(opt.api).catch(() => {}); }
    });
    if (ui.vinqBtn) ui.vinqBtn.addEventListener('click', vinQuick);
    if (ui.vinqIn) ui.vinqIn.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); vinQuick(); } });
    ui.missOpen.addEventListener('click', () => (ui.miss.hidden ? openMissing() : closeMissing()));
    ui.mCancel.addEventListener('click', closeMissing);
    ui.mSend.addEventListener('click', () => sendMissing(false));
    ui.mAnyway.addEventListener('click', () => sendMissing(true));
    ui.mVinBtn.addEventListener('click', fillFromVin);
    ui.mVin.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); fillFromVin(); } });
    ui.mFoundList.addEventListener('click', (e) => {
        const b = e.target.closest('[data-model]');
        if (b) pickFound(b.dataset.model);
    });
    [ui.mMake, ui.mModel, ui.mYear].forEach((el) => el.addEventListener('input', () => { ui.mFound.hidden = true; delete el.dataset.fromVin; }));

    /* ── restore the last car (shared by every car tool) ────────────────── */

    const api = {
        value,
        clear: () => clear(false),
        setCoverage(fn) { coverage = typeof fn === 'function' ? fn : null; renderCoverage(value()); },
        async select(v, o) {
            if (!v || !v.modelId) return value();
            try { await loadCatalog(opt.api); } catch (e) { return value(); }
            return pickModel(v, (o && o.how) || 'select');
        },
        async selectMake(makeId, o) {
            try { await loadCatalog(opt.api); } catch (e) { return value(); }
            if (!catalog.make(makeId)) return value();
            choose({ kind: 'make', makeId }, (o && o.how) || 'select');
            return value();
        },
        setText(text) {
            clear(true);
            ui.q.value = String(text || '');
            state.text = ui.q.value;
            renderFields();
            emit();
        },
        profileCar: () => profileCar,
        /** The owner's garage as this page knows it (./garage.js). */
        garage: () => garage.slice(),
        catalog: () => catalog,
        ready: () => loadCatalog(opt.api),
    };
    root.carPicker = api;

    if (opt.remember && settings()) {
        const saved = settings().load(STORE_NS, STORE_KEY, null);
        if (saved && saved.modelId && saved.labels) {
            state.makeId = saved.makeId || null;
            state.modelId = saved.modelId;
            state.generationId = saved.generationId || null;
            state.engineId = saved.engineId || null;
            state.year = saved.year || null;
            restoredLabels = saved.labels;
            ui.q.value = saved.labels.make || '';
            ui.mq.value = saved.labels.model || '';
            state.text = [saved.labels.make, saved.labels.model].filter(Boolean).join(' ');
            renderFields();
            source = 'browser';
            rememberedAtMount = saved;
            emit({ restored: true });
            const idle = window.requestIdleCallback || ((f) => setTimeout(f, 300));
            idle(() => loadCatalog(opt.api).then(() => {
                if (source !== 'browser' || state.touched) return;
                hydrate(); emit({ restored: true });
            }).catch(() => {}));
        }
    }
    try { root.dispatchEvent(new CustomEvent('car-picker:ready', { detail: value(), bubbles: true })); } catch (e) { /* old engines */ }

    if (opt.profile) {
        const settle = (b) => {
            mergeCars();
            /* the account's main car; without one — the browser logbook's first (its current car) */
            const main = b.loggedIn && b.car && b.car.make_id ? b.car : (garageChoices(garage)[0] || null);
            profileCar = prefillOf(main, garage);
            const how = choosePrefill({ remembered: source === 'browser' ? rememberedAtMount : null, profile: profileCar, touched: state.touched });
            if (how === 'profile') { applyProfile(profileCar); return; }
            /* the browser's car stays — and if it IS the owner's car, the tool
               now learns its gearbox and mileage too */
            const v = value();
            if (v.profileCar) emit(source === 'browser' ? { restored: true } : undefined);
            else renderProfile(v);
        };
        whenProfileCar((b) => {
            loggedIn = b.loggedIn;
            acctCars = b.loggedIn && Array.isArray(b.cars) ? b.cars : [];
            if (!logbookHere()) { settle(b); return; }
            import('./car-context.js')
                .then((m) => m.logbookPickerCars(acctCars))
                .then((list) => { lbCars = list; }, () => { lbCars = []; })
                .then(() => {
                    settle(b);
                    if (lbCars.length) ae('feature_use', { slug: opt.slug, action: 'car_garage_browser', n: lbCars.length });
                });
        });
        if (ui.profOther) ui.profOther.addEventListener('click', () => {
            clear(false);
            ui.q.focus();
            ae('feature_use', { slug: opt.slug, action: 'car_profile_other' });
        });
        if (ui.garage) ui.garage.addEventListener('change', () => { if (ui.garage.value) useGarageCar(ui.garage.value); });
        /* the garage changed on this page (the block on /profile, the logbook) */
        document.addEventListener('garage:change', (e) => {
            const j = e.detail || {};
            if (!Array.isArray(j.cars)) return;
            acctCars = j.cars;
            mergeCars();
            profileCar = prefillOf(j.car || null, garage);
            renderProfile(value());
        });
        if (ui.psaveBtn) ui.psaveBtn.addEventListener('click', saveToProfile);
    }
    return api;
}

function mountAll() {
    document.querySelectorAll('[data-car-picker]').forEach((el) => {
        try { mount(el); } catch (e) { window.__handleError ? window.__handleError(e, 'car_picker_mount') : console.error(e); }
    });
}

window.CarPicker = { mount, mountAll, loadCatalog: (api) => loadCatalog(api) };

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mountAll);
else mountAll();
